import React from 'react';
import { Table, Column } from '@tanstack/react-table';
import { Button } from '../../../components/ui/button';
import { StudyData } from '@/types/types';
import { FilterWrapper } from '../../table-common/filters/filterWrapper';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Checkbox } from '../../../components/ui/checkbox';

interface ModalityFilterProps {
  column: any;
  table: Table<StudyData>;
}

export const ModalityFilter: React.FC<ModalityFilterProps> = ({
  column,
  table,
}) => {
  const filterValue = column.getFilterValue() as string[] | undefined;
  const modalities = React.useMemo(() => {
    return Array.from(
      new Set(
        table
          .getPreFilteredRowModel()
          .rows.map((row) => row.original.Modality)
          .filter((m): m is string => !!m)
      )
    );
  }, [table]);

  const selectedModalities = filterValue ?? [];

  return (
    <FilterWrapper
      filterValue={filterValue}
      onClear={() => column.setFilterValue(undefined)}
      className="w-3/4"
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="!px-2 !pr-4 w-full border-2 border-black rounded h-8 text-sm font-normal flex justify-start overflow-hidden whitespace-nowrap text-ellipsis"
            title={
              selectedModalities.length > 0
                ? `${selectedModalities.length} selected`
                : 'Select Modality'
            }
          >
            {selectedModalities.length > 0
              ? `${selectedModalities.length} selected`
              : 'Select Modality'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-2">
          <div className="space-y-2 max-h-48 overflow-auto">
            {modalities.map((modality) => (
              <div key={modality} className="flex items-center space-x-2">
                <Checkbox
                  id={modality}
                  checked={selectedModalities.includes(modality)}
                  onCheckedChange={(checked) => {
                    let newSelected: string[];
                    if (checked) {
                      newSelected = [...selectedModalities, modality];
                    } else {
                      newSelected = selectedModalities.filter(
                        (m) => m !== modality
                      );
                    }
                    column.setFilterValue(
                      newSelected.length ? newSelected : undefined
                    );
                  }}
                />
                <label
                  htmlFor={modality}
                  className="text-sm font-medium leading-none"
                >
                  {modality}
                </label>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </FilterWrapper>
  );
};

export function InstanceCountFilter({ column }: { column: Column<any> }) {
  const filterValue = (column.getFilterValue() as [string, string]) || ['', ''];
  const hasFilter = filterValue[0] !== '' || filterValue[1] !== '';

  const setFilter = (min: string, max: string) => {
    if (min === '' && max === '') {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue([min, max]);
    }
  };

  return (
    <FilterWrapper
      filterValue={hasFilter ? filterValue : undefined}
      onClear={() => column.setFilterValue(undefined)}
      className="w-4/4"
      clearButtonInside={false}
    >
      <div className="flex gap-2 items-center">
        <input
          type="number"
          min={0}
          value={filterValue[0]}
          placeholder="Min"
          className="w-20 border-2 border-black rounded px-2 py-1 text-sm font-normal"
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || Number(val) >= 0) {
              setFilter(val, filterValue[1]);
            }
          }}
        />
        <span>to</span>
        <input
          type="number"
          min={1}
          value={filterValue[1]}
          placeholder="Max"
          className="w-20 border-2 border-black rounded px-2 py-1 text-sm font-normal"
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || Number(val) >= 1) {
              setFilter(filterValue[0], val);
            }
          }}
        />
      </div>
    </FilterWrapper>
  );
}
